import React, { useEffect } from "react";

import { InfoSmIcon } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useOrganizationCarbonEmissions } from "Reducers/carbonEmissions/hooks/useOrganizationCarbonEmissions";

import CarbonEmissionsGraph from "./carbonEmissionsGraph";

export const PageWrapper = styled.div`
  --aside-width: 356px;
  --content-padding: 40px;
  --column-gap: 32px;

  display: grid;
  grid-template-columns: var(--aside-width) 1fr;
  min-height: 100vh;
  gap: var(--column-gap);
  margin-left: calc(-1 * var(--content-padding));
`;

export const MainContent = styled.main`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 40px;
`;

export const MainHeading = styled.h1`
  color: var(--mode-text-neutral-default);
  font-size: var(--fs-xxl);
  font-weight: 700;
`;

export const AsideWrapper = styled.div`
  background-color: var(--mode-bg-neutral-weakest);
  padding: 32px 24px;
  display: inline-flex;
  flex-direction: column;
`;

export const AsideSection = styled.section`
  margin-top: 6px;
  margin-bottom: 24px;
  border-radius: 8px;
  border: 1px solid var(--mode-border-neutral-weakest);
  padding: 16px 24px 24px;
`;

export const AsideHeading = styled.h2`
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const AsideText = styled.p`
  color: var(--mode-text-neutral-default, #181e21);
  font-size: var(--fs-m, 15px);
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;

export const AsideLink = styled.a`
  text-decoration: none;
`;

export const SectionContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

export const Section = styled.section`
  position: relative;
  padding: 24px 32px;
  flex: 1;
  width: 50%;
  border-radius: 8px;
  background: var(--mode-bg-neutral-weakest);
  color: var(--mode-text-neutral-default, #181e21);
  font-weight: 700;
`;

export const SectionHeading = styled.h2`
  font-size: 15px;
  margin-top: 0;
`;

export const SectionHeadingSpan = styled.span`
  color: var(--mode-text-neutral-weakest);
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  margin-left: 8px;
`;

export const SectionValue = styled.h1`
  font-size: 20px;
  font-style: normal;
  margin-top: 16px;
  margin-bottom: 0;
`;

export const SectionValueUnit = styled.span`
  color: var(--mode-text-neutral-weakest);
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  margin-left: 16px;
`;

export const IconWrapper = styled.span`
  margin-left: 6px;
  position: relative;
  top: 6px;
`;

const Page = () => {
  const intl = useIntl();
  const { organizationId } = useParams<{ organizationId: string }>();

  const {
    loadData,
    isLoading,
    previousYearTotal,
    currentYearTotal,
    monthlyChartData
  } = useOrganizationCarbonEmissions(organizationId);

  useEffect(() => {
    if (organizationId && !isLoading && !monthlyChartData) {
      loadData();
    }
  }, [organizationId, loadData, isLoading, monthlyChartData]);

  // Helper for formatting numbers
  const formatNumber = (value: number | undefined) => {
    return value !== undefined ? value.toFixed(2) : "0.00";
  };

  // Compute years for display
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  // Create default empty chart data to avoid undefined error
  const safeChartData = monthlyChartData || [];

  return (
    <PageWrapper>
      <AsideWrapper>
        <MainHeading>
          {intl.formatMessage({
            id: "carbon.emissions.overview.title"
          })}
        </MainHeading>
        <AsideSection>
          <AsideHeading>
            {intl.formatMessage({
              id: "carbon.emissions.calculation.title"
            })}
            <InfoSmIcon fill="var(--mode-vector-neutral-default)" />
          </AsideHeading>
          <AsideText>
            <FormattedMessage
              id="carbon.emissions.calculation.explanation"
              values={{
                a: chunks => <AsideLink href="/">{chunks}</AsideLink>
              }}
            />
          </AsideText>
        </AsideSection>
        <AsideSection>
          <AsideHeading>
            {intl.formatMessage({
              id: "carbon.emissions.affects.title"
            })}
          </AsideHeading>
          <AsideText>
            <FormattedMessage id="carbon.emissions.affects.explanation" />
          </AsideText>
        </AsideSection>
      </AsideWrapper>
      <MainContent>
        <MainHeading>
          {intl.formatMessage({
            id: "carbon.emissions.main.title"
          })}
        </MainHeading>
        <SectionContainer>
          <Section>
            <SectionHeading>
              {intl.formatMessage({
                id: "carbon.emissions.thisYear.title"
              })}
              <SectionHeadingSpan>Jan {currentYear} - Today</SectionHeadingSpan>
            </SectionHeading>
            <SectionValue>
              {formatNumber(currentYearTotal)}
              <SectionValueUnit>
                {intl.formatMessage({
                  id: "carbon.emissions.units",
                  defaultMessage: "Kg CO2eq"
                })}
              </SectionValueUnit>
              <IconWrapper>
                <InfoSmIcon fill="var(--mode-vector-neutral-default)" />
              </IconWrapper>
            </SectionValue>
          </Section>
          <Section>
            <SectionHeading>
              {intl.formatMessage({
                id: "carbon.emissions.lastYear.title",
                defaultMessage: "Last year total"
              })}
              <SectionHeadingSpan>
                Jan {lastYear} - Dec {lastYear}
              </SectionHeadingSpan>
            </SectionHeading>
            <SectionValue>
              {formatNumber(previousYearTotal)}
              <SectionValueUnit>
                {intl.formatMessage({
                  id: "carbon.emissions.units",
                  defaultMessage: "Kg CO2eq"
                })}
              </SectionValueUnit>
              <IconWrapper>
                <InfoSmIcon fill="var(--mode-vector-neutral-default)" />
              </IconWrapper>
            </SectionValue>
          </Section>
        </SectionContainer>
        <SectionContainer>
          <Section>
            <SectionHeading>
              {intl.formatMessage({
                id: "carbon.emissions.graph.title"
              })}
            </SectionHeading>
            <CarbonEmissionsGraph chartData={safeChartData} />
          </Section>
        </SectionContainer>
      </MainContent>
    </PageWrapper>
  );
};

export default Page;
